import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { ToastContext } from '../../context/ToastContext';
import { fetchObituaries, fetchOrders } from '../../utils/api';
import { formatCurrency } from '../../utils/formatters';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Card from '../../components/common/Card';
import Modal from '../../components/common/Modal';
import { FileText, ShoppingBag, Wallet, KeyRound, Bell } from 'lucide-react';

const ProfilePage = () => {
  const { user, updateProfile } = useContext(AuthContext);
  const { showToast } = useContext(ToastContext);

  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
  });
  const [errors, setErrors] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  // 프로필 통계
  const [stats, setStats] = useState({ obituaries: 0, orders: 0, amount: 0 });

  // 비밀번호 변경 모달(mock)
  const [showPwModal, setShowPwModal] = useState(false);
  const [pwData, setPwData] = useState({ current: '', next: '', confirm: '' });
  const [pwErrors, setPwErrors] = useState({});
  const [pwSaving, setPwSaving] = useState(false);

  // 알림 설정(mock)
  const [notifications, setNotifications] = useState({
    kakao: true,
    email: true,
    sms: false,
  });

  useEffect(() => {
    if (!user) return;
    let active = true;
    (async () => {
      try {
        const [obits, orders] = await Promise.all([
          fetchObituaries(),
          fetchOrders(),
        ]);
        if (!active) return;
        const myOrders = orders.filter((o) => o.userId === user.id);
        setStats({
          obituaries: obits.filter((o) => o.userId === user.id).length,
          orders: myOrders.length,
          amount: myOrders.reduce((sum, o) => sum + (o.totalAmount || 0), 0),
        });
      } catch (error) {
        // 통계는 부가정보이므로 실패 무시
      }
    })();
    return () => {
      active = false;
    };
  }, [user]);

  // 변경 여부(dirty check)
  const isDirty =
    formData.name !== (user?.name || '') ||
    formData.phone !== (user?.phone || '');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const formatPhoneNumber = (value) => {
    const numbers = value.replace(/[^\d]/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 7) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setFormData((prev) => ({ ...prev, phone: formatted }));
    if (errors.phone) {
      setErrors((prev) => ({ ...prev, phone: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = '이름을 입력해주세요.';
    }
    if (!formData.phone) {
      newErrors.phone = '전화번호를 입력해주세요.';
    } else if (!/^01[0-9]-\d{3,4}-\d{4}$/.test(formData.phone)) {
      newErrors.phone = '올바른 전화번호 형식이 아닙니다.';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      showToast('입력 정보를 확인해주세요.', 'error');
      return;
    }
    if (!isDirty) {
      setIsEditing(false);
      return;
    }
    setSaving(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 600)); // mock 저장 지연
      updateProfile(formData);
      setIsEditing(false);
      showToast('회원정보가 수정되었습니다.', 'success');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      name: user?.name || '',
      email: user?.email || '',
      phone: user?.phone || '',
    });
    setErrors({});
    setIsEditing(false);
  };

  // 비밀번호 변경(mock)
  const handlePwChange = (e) => {
    const { name, value } = e.target;
    setPwData((prev) => ({ ...prev, [name]: value }));
    if (pwErrors[name]) setPwErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handlePwSubmit = async () => {
    const errs = {};
    if (!pwData.current) errs.current = '현재 비밀번호를 입력해주세요.';
    if (!pwData.next || pwData.next.length < 6)
      errs.next = '새 비밀번호는 6자 이상이어야 합니다.';
    if (pwData.next !== pwData.confirm)
      errs.confirm = '새 비밀번호가 일치하지 않습니다.';
    setPwErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setPwSaving(true);
    await new Promise((resolve) => setTimeout(resolve, 600));
    setPwSaving(false);
    setShowPwModal(false);
    setPwData({ current: '', next: '', confirm: '' });
    showToast('비밀번호가 변경되었습니다.', 'success');
  };

  const toggleNotification = (key) => {
    setNotifications((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      showToast(
        `${key === 'kakao' ? '카카오톡' : key === 'email' ? '이메일' : 'SMS'} 알림이 ${
          next[key] ? '켜졌습니다' : '꺼졌습니다'
        }.`,
        'success'
      );
      return next;
    });
  };

  const statCards = [
    { icon: FileText, label: '작성 부고', value: `${stats.obituaries}건` },
    { icon: ShoppingBag, label: '주문', value: `${stats.orders}건` },
    { icon: Wallet, label: '총 결제금액', value: formatCurrency(stats.amount) },
  ];

  return (
    <Card>
      <div className="p-6">
        <h2 className="text-xl font-bold text-primary mb-6">회원정보</h2>

        {/* 프로필 통계 */}
        <div className="mb-6 grid grid-cols-3 gap-3">
          {statCards.map((s) => {
            const Icon = s.icon;
            return (
              <div
                key={s.label}
                className="rounded-lg border border-accent-200 bg-secondary-50 p-3 text-center"
              >
                <Icon className="mx-auto mb-1 h-5 w-5 text-primary-600" />
                <p className="text-xs text-accent-600">{s.label}</p>
                <p className="text-sm font-bold text-primary">{s.value}</p>
              </div>
            );
          })}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            type="text"
            name="name"
            label="이름"
            value={formData.name}
            onChange={handleChange}
            error={errors.name}
            disabled={!isEditing}
            required
          />

          <Input
            type="email"
            name="email"
            label="이메일"
            value={formData.email}
            onChange={handleChange}
            disabled={true}
            required
          />
          <p className="text-xs text-accent-600 -mt-2">
            이메일은 변경할 수 없습니다.
          </p>

          <Input
            type="tel"
            name="phone"
            label="전화번호"
            value={formData.phone}
            onChange={handlePhoneChange}
            error={errors.phone}
            disabled={!isEditing}
            placeholder="010-1234-5678"
            required
          />

          <div className="pt-4 flex gap-3">
            {!isEditing ? (
              <Button
                type="button"
                variant="primary"
                onClick={() => setIsEditing(true)}
              >
                수정하기
              </Button>
            ) : (
              <>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={saving || !isDirty}
                >
                  {saving ? '저장 중...' : '저장하기'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                  disabled={saving}
                >
                  취소
                </Button>
              </>
            )}
          </div>
        </form>

        {/* 보안 설정 */}
        <div className="mt-8 pt-6 border-t border-accent-200">
          <h3 className="mb-3 flex items-center gap-2 text-sm font-medium text-primary">
            <KeyRound className="h-4 w-4" /> 보안
          </h3>
          <Button variant="outline" size="sm" onClick={() => setShowPwModal(true)}>
            비밀번호 변경
          </Button>
        </div>

        {/* 알림 설정 (mock) */}
        <div className="mt-8 pt-6 border-t border-accent-200">
          <h3 className="mb-3 flex items-center gap-2 text-sm font-medium text-primary">
            <Bell className="h-4 w-4" /> 알림 설정
          </h3>
          <div className="space-y-3">
            {[
              { key: 'kakao', label: '카카오톡 알림' },
              { key: 'email', label: '이메일 알림' },
              { key: 'sms', label: 'SMS 알림' },
            ].map((n) => (
              <div
                key={n.key}
                className="flex items-center justify-between"
              >
                <span className="text-sm text-accent-700">{n.label}</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={notifications[n.key]}
                  aria-label={`${n.label} 토글`}
                  onClick={() => toggleNotification(n.key)}
                  className={`relative h-6 w-11 rounded-full transition-colors ${
                    notifications[n.key] ? 'bg-primary-600' : 'bg-accent-300'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${
                      notifications[n.key]
                        ? 'translate-x-5'
                        : 'translate-x-0.5'
                    }`}
                  />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* 가입 정보 */}
        <div className="mt-8 pt-6 border-t border-accent-200">
          <h3 className="text-sm font-medium text-primary mb-3">가입 정보</h3>
          <div className="space-y-2 text-sm text-accent-600">
            <p>
              가입일:{' '}
              {user?.createdAt
                ? new Date(user.createdAt).toLocaleDateString('ko-KR')
                : '-'}
            </p>
            <p>회원 등급: {user?.role === 'admin' ? '관리자' : '일반 회원'}</p>
          </div>
        </div>
      </div>

      {/* 비밀번호 변경 모달 (mock) */}
      <Modal
        isOpen={showPwModal}
        onClose={() => !pwSaving && setShowPwModal(false)}
        title="비밀번호 변경"
        size="sm"
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setShowPwModal(false)}
              disabled={pwSaving}
            >
              취소
            </Button>
            <Button
              variant="primary"
              onClick={handlePwSubmit}
              disabled={pwSaving}
            >
              {pwSaving ? '변경 중...' : '변경하기'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input
            type="password"
            name="current"
            label="현재 비밀번호"
            value={pwData.current}
            onChange={handlePwChange}
            error={pwErrors.current}
          />
          <Input
            type="password"
            name="next"
            label="새 비밀번호"
            value={pwData.next}
            onChange={handlePwChange}
            error={pwErrors.next}
            placeholder="6자 이상"
          />
          <Input
            type="password"
            name="confirm"
            label="새 비밀번호 확인"
            value={pwData.confirm}
            onChange={handlePwChange}
            error={pwErrors.confirm}
          />
        </div>
      </Modal>
    </Card>
  );
};

export default ProfilePage;
